import { Ansicht, GutachtenAnsicht, GutachtenFilter, GutachtenTyp, MustervorlagenFilter, ProbenAnsicht } from '@/api/model';
import { Probentyp, ProbentypHelpers } from '@/types/probentyp';
import type { RouteLocationRaw, Router, RouteRecordRaw } from 'vue-router';
import { ROUTE_NAMES } from './routeNames';

// Definiere wiederverwendbare Route-Constraints
export const routeConstraints = {
    numerischeId: '\\d+', // Constraint für positive numerische IDs
    numerischeIdMitVorzeichen: '-?\\d+' // Constraint für temporäre negative IDs und positive IDs
};

/**
 * Hilfsfunktion zum Erstellen einer Route mit optionalem Kunde-Parameter
 */
function createRouteMitKunde(path: string, name: string, component: () => Promise<any>, additionalProps?: (route: any) => Record<string, any>): RouteRecordRaw {
    return {
        path: `/:kunde?${path}`,
        name,
        component,
        props: (route) => ({
            kunde: route.params.kunde || null,
            ...(additionalProps ? additionalProps(route) : {})
        })
    };
}

function createRedirectRouteMitKunde(path: string, redirectRouteName: string): RouteRecordRaw {
    return {
        path: `/:kunde?${path}`,
        redirect: (to): RouteLocationRaw => ({
            name: redirectRouteName,
            params: {
                ...to.params,
                kunde: to.params.kunde ?? null
            }
        })
    };
}

/**
 * Erstellt alle Proben-Routen
 */
export function createProbenRouten(): RouteRecordRaw[] {
    return [
        // Proben Liste
        createRouteMitKunde('/proben', ROUTE_NAMES.PROBEN.LISTE, () => import('@/views/pages/proben/Proben.vue')),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/probe/proben', ROUTE_NAMES.PROBEN.LISTE),

        // Proben Bearbeiten
        createRouteMitKunde(
            `/proben/bearbeiten/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.PROBEN.EDIT,
            () => import('@/views/pages/proben/ProbenEdit.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                istNeu: false,
                probentyp: Probentyp.Probe
            })
        ),

        // Probennummer zuordnen
        createRouteMitKunde('/proben/neu/zuordnen', ROUTE_NAMES.PROBEN.PROBENNUMMERN_ZUORDNEN, () => import('@/views/pages/proben/ProbenNrZuordnen.vue')),

        // Proben Neu
        createRouteMitKunde(
            '/proben/neu',
            ROUTE_NAMES.PROBEN.NEU,
            () => import('@/views/pages/proben/ProbenEdit.vue'),
            () => ({
                probenNr: null,
                istNeu: true,
                probentyp: Probentyp.Probe
            })
        ),

        // Proben Excel Import
        // Bewusst nicht /proben/excel-import, da sonst das aktive Element im Menü nicht richtig ermittelt wird
        createRouteMitKunde(
            '/proben-excel-import',
            ROUTE_NAMES.PROBEN.EXCEL_IMPORT,
            () => import('@/views/pages/proben/ExcelImport.vue'),
            () => ({
                probentyp: Probentyp.Probe
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/probe/excel-import', ROUTE_NAMES.PROBEN.EXCEL_IMPORT),

        // Proben Mischprobe Bearbeiten
        createRouteMitKunde(
            `/proben/mischprobe-bearbeiten/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.PROBEN.MISCHPROBE_BEARBEITEN,
            () => import('@/views/pages/proben/Mischprobe.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                probentyp: Probentyp.Probe
            })
        ),

        // Proben Testzeitpunkt Bearbeiten
        createRouteMitKunde(
            `/proben/testzeitpunkt-bearbeiten/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.PROBEN.TESTZEITPUNKT_BEARBEITEN,
            () => import('@/views/pages/proben/Testzeitpunkt.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                probentyp: Probentyp.Probe
            })
        ),

        // Textvorlagen
        createRouteMitKunde(
            '/proben/textvorlagen',
            ROUTE_NAMES.PROBEN.TEXTVORLAGEN,
            () => import('@/views/pages/textvorlagen/Textvorlagen.vue'),
            () => ({
                probentyp: Probentyp.Probe
            })
        ),

        // Prüfpläne
        createRouteMitKunde(
            'proben/pruefplaene',
            ROUTE_NAMES.PROBEN.PRUEFPLAENE,
            () => import('@/views/pages/pruefplan/Pruefplaene.vue'),
            () => ({
                probentyp: Probentyp.Probe
            })
        ),

        // Nicht freigegebene Proben Warnung
        createRouteMitKunde(
            '/proben/nicht-freigegebene-proben-warnung',
            ROUTE_NAMES.PROBEN.NICHT_FREIGEGEBENE_WARNUNG,
            () => import('@/views/pages/proben/NichtFreigegebeneProbenWarnung.vue'),
            (route) => ({
                kunde: route.params.kunde || null
            })
        )
    ];
}

/**
 * Erstellt alle Mustervorlagen-Routen (parallel zu Proben)
 */
export function createMustervorlagenRouten(): RouteRecordRaw[] {
    return [
        // Mustervorlagen Liste
        createRouteMitKunde('/mustervorlagen', ROUTE_NAMES.MUSTERVORLAGEN.LISTE, () => import('@/views/pages/proben/Mustervorlagen.vue')),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/mustervorlage/mustervorlagen', ROUTE_NAMES.MUSTERVORLAGEN.LISTE),

        // Mustervorlagen Filter
        createRouteMitKunde('/mustervorlage/filter', ROUTE_NAMES.MUSTERVORLAGEN.FILTER, () => import('@/views/pages/proben/MustervorlagenFilter.vue')),

        // Mustervorlagen Bearbeiten
        createRouteMitKunde(
            `/mustervorlagen/bearbeiten/:mustervorlagenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.MUSTERVORLAGEN.EDIT,
            () => import('@/views/pages/proben/ProbenEdit.vue'),
            (route) => ({
                probenNr: route.params.mustervorlagenNr ? parseInt(route.params.mustervorlagenNr) : null,
                istNeu: false,
                probentyp: Probentyp.Mustervorlage
            })
        ),

        // Mustervorlagen Neu
        createRouteMitKunde(
            '/mustervorlagen/neu',
            ROUTE_NAMES.MUSTERVORLAGEN.NEU,
            () => import('@/views/pages/proben/ProbenEdit.vue'),
            () => ({
                probenNr: null,
                istNeu: true,
                probentyp: Probentyp.Mustervorlage
            })
        ),

        // Mustervorlagen Mischprobe Bearbeiten
        createRouteMitKunde(
            `/mustervorlagen/mischprobe-bearbeiten/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.MUSTERVORLAGEN.MISCHPROBE_BEARBEITEN,
            () => import('@/views/pages/proben/Mischprobe.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                probentyp: Probentyp.Mustervorlage
            })
        ),

        createRouteMitKunde(
            '/mustervorlagen/textvorlagen',
            ROUTE_NAMES.MUSTERVORLAGEN.TEXTVORLAGEN,
            () => import('@/views/pages/textvorlagen/Textvorlagen.vue'),
            () => ({
                probentyp: Probentyp.Mustervorlage
            })
        ),

        createRouteMitKunde(
            'mustervorlagen/pruefplaene',
            ROUTE_NAMES.MUSTERVORLAGEN.PRUEFPLAENE,
            () => import('@/views/pages/pruefplan/Pruefplaene.vue'),
            () => ({
                probentyp: Probentyp.Mustervorlage
            })
        )
    ];
}

export function createEtikettenRouten(): RouteRecordRaw[] {
    return [
        createRouteMitKunde('/etikettendruck', ROUTE_NAMES.ETIKETTEN.LISTE, () => import('@/views/pages/etikettendruck/Etiketten.vue')),
        createRouteMitKunde('/etikettendruck/filter', ROUTE_NAMES.ETIKETTEN.FILTER, () => import('@/views/pages/etikettendruck/EtikettendruckFilter.vue')),
        createRouteMitKunde('/probenbegleitschein-druck', ROUTE_NAMES.PROBENBEGLEITSCHEIN.LISTE, () => import('@/views/pages/etikettendruck/Probenbegleitschein.vue')),
        createRouteMitKunde('/probenbegleitschein-druck/filter', ROUTE_NAMES.PROBENBEGLEITSCHEIN.FILTER, () => import('@/views/pages/etikettendruck/ProbenbegleitscheinDruckFilter.vue'))
    ];
}

export function createBegehungsberichteRouten(): RouteRecordRaw[] {
    return [
        createRouteMitKunde('/begehungsberichte', ROUTE_NAMES.BEGEHUNGSBERICHTE.LISTE, () => import('@/views/pages/begehungsberichte/Begehungsberichte.vue')),
        createRouteMitKunde(
            `/begehungsberichte/:id(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.BEGEHUNGSBERICHTE.DETAIL,
            () => import('@/views/pages/begehungsberichte/Begehungsbericht.vue'),
            (route) => ({
                id: parseInt(route.params.id)
            })
        )
    ];
}

export function createGutachtenRouten(): RouteRecordRaw[] {
    return [
        createRouteMitKunde('/gutachten/beauftragen', ROUTE_NAMES.GUTACHTEN.ERFASSEN, () => import('@/views/pages/gutachten/GutachtenErfassen.vue')),

        createRouteMitKunde('/gutachten/typ-auswahl', ROUTE_NAMES.GUTACHTEN.TYP_AUSWAHL, () => import('@/views/pages/gutachten/GutachtenTypAuswahl.vue')),

        createRouteMitKunde(
            '/gutachten/neu/:gutachtenTypID',
            ROUTE_NAMES.GUTACHTEN.NEU,
            () => import('@/views/pages/gutachten/GutachtenEdit.vue'),
            (route) => ({
                gutachtenVersionID: null,
                gutachtenTypID: route.params.gutachtenTypID as GutachtenTyp,
                istNeu: true
            })
        ),

        createRouteMitKunde(
            `/gutachten/bearbeiten/:id(${routeConstraints.numerischeIdMitVorzeichen})`,
            ROUTE_NAMES.GUTACHTEN.EDIT,
            () => import('@/views/pages/gutachten/GutachtenEdit.vue'),
            (route) => ({
                gutachtenVersionID: parseInt(route.params.id),
                gutachtenTypID: null,
                istNeu: false
            })
        ),

        createRouteMitKunde('/gutachten/in-bearbeitung', ROUTE_NAMES.GUTACHTEN.IN_BEARBEITUNG, () => import('@/views/pages/gutachten/GutachtenInBearbeitung.vue')),

        createRouteMitKunde(
            `/gutachten/in-bearbeitung/:id(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.GUTACHTEN.DETAIL_IN_BEARBEITUNG,
            () => import('@/views/pages/gutachten/GutachtenErgebnis.vue'),
            (route) => ({
                id: parseInt(route.params.id),
                ansicht: GutachtenAnsicht.InBearbeitung
            })
        ),

        createRouteMitKunde('/gutachten/ergebnisse', ROUTE_NAMES.GUTACHTEN.ERGEBNISSE, () => import('@/views/pages/gutachten/GutachtenErgebnisse.vue')),

        createRouteMitKunde('/gutachten/rohstoffspezifikationen', ROUTE_NAMES.GUTACHTEN.ROHSTOFFSPEZIFIKATIONEN, () => import('@/views/pages/gutachten/GutachtenRohstoffspezifikationen.vue')),

        createRouteMitKunde('/gutachten/ergebnisse/filter', ROUTE_NAMES.GUTACHTEN.FILTER, () => import('@/views/pages/gutachten/GutachtenErgebnisseFilter.vue')),

        createRouteMitKunde(
            `/gutachten/ergebnisse/:id(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.GUTACHTEN.DETAIL,
            () => import('@/views/pages/gutachten/GutachtenErgebnis.vue'),
            (route) => ({
                id: parseInt(route.params.id),
                ansicht: GutachtenAnsicht.Ergebnisse
            })
        )
    ];
}

/**
 * Erstellt alle Einstellungen-Routen
 */
export function createKundenbereichRouten(): RouteRecordRaw[] {
    return [
        {
            path: '/:kunde?/kundenbereich',
            name: ROUTE_NAMES.KUNDENBEREICH.UEBERSICHT,
            component: () => import('@/views/pages/kundenbereich/Kundenbereich.vue'),
            props: (route) => ({ kunde: route.params.kunde || null }),
            children: [
                // Diese Route sorgt dafür, dass beim Aufruf von /kundenbereich auf /kundenbereich/ergebnistabellen-layout umgeleitet wird
                {
                    name: 'kundenbereich-umleitung',
                    path: '',
                    redirect: (route) => ({
                        name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_ERGEBNIS,
                        params: { kunde: route.params.kunde }
                    })
                },
                {
                    path: 'ergebnistabellen-layout',
                    name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_ERGEBNIS,
                    component: () => import('@/components/kundenbereich/TabellenDefinitionen.vue'),
                    props: (route) => ({
                        kunde: route.params.kunde || null,
                        ansicht: Ansicht.ErgebnisTabellen
                    })
                },
                {
                    path: 'erfassentabellen-layout',
                    name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_ERFASSEN,
                    component: () => import('@/components/kundenbereich/TabellenDefinitionen.vue'),
                    props: (route) => ({
                        kunde: route.params.kunde || null,
                        ansicht: Ansicht.ErfassenTabellen
                    })
                },
                {
                    path: 'excel-export-optionen',
                    name: ROUTE_NAMES.KUNDENBEREICH.EXCEL_EXPORT,
                    component: () => import('@/components/kundenbereich/ExcelExportOptionen.vue'),
                    props: (route) => ({
                        kunde: route.params.kunde
                    }),
                    children: [
                        // Diese Route sorgt dafür, dass beim Aufruf von /excel-export-optionen auf /excel-export-optionen/layout umgeleitet wird
                        {
                            name: 'excel-export-optionen-umleitung',
                            path: '',
                            redirect: (route) => ({
                                name: ROUTE_NAMES.KUNDENBEREICH.EXCEL_EXPORT_LAYOUT,
                                params: { kunde: route.params.kunde },
                                ansicht: Ansicht.ExcelExport
                            })
                        },
                        {
                            path: 'layout',
                            name: ROUTE_NAMES.KUNDENBEREICH.EXCEL_EXPORT_LAYOUT,
                            component: () => import('@/components/kundenbereich/TabellenDefinitionen.vue'),
                            props: (route) => ({
                                kunde: route.params.kunde,
                                ansicht: Ansicht.ExcelExport
                            })
                        },
                        {
                            path: 'parameter',
                            name: ROUTE_NAMES.KUNDENBEREICH.EXCEL_EXPORT_PARAMETER,
                            component: () => import('@/components/kundenbereich/ExcelExportOptionenParameter.vue')
                        }
                    ]
                },
                {
                    path: 'gutachten-erfassentabellen-layout',
                    name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_GUTACHTEN_ERFASSEN,
                    component: () => import('@/components/kundenbereich/TabellenDefinitionen.vue'),
                    props: (route) => ({
                        kunde: route.params.kunde || null,
                        ansicht: Ansicht.GutachtenErfassenTabellen
                    })
                },
                {
                    path: 'gutachten-versionen-ergebnistabellen-layout',
                    name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_GUTACHTEN_ERGEBNISSE,
                    component: () => import('@/components/kundenbereich/TabellenDefinitionen.vue'),
                    props: (route) => ({
                        kunde: route.params.kunde || null,
                        ansicht: Ansicht.GutachtenVersionenErgebnistabellen
                    })
                },
                {
                    path: 'eigenschaftbenennung',
                    name: ROUTE_NAMES.KUNDENBEREICH.EIGENSCHAFTEN,
                    component: () => import('@/components/kundenbereich/EigenschaftBezeichnungen.vue'),
                    props: (route) => ({ kunde: route.params.kunde || null })
                },
                {
                    path: 'kennwort-aendern',
                    name: ROUTE_NAMES.KUNDENBEREICH.PASSWORT,
                    component: () => import('@/components/kundenbereich/PasswortAendern.vue')
                }
            ]
        }
    ];
}

/**
 * Erstellt alle Ergebnis-Routen
 */
export function createErgebnisRouten(): RouteRecordRaw[] {
    return [
        createRouteMitKunde(
            '/proben-in-zustellung',
            ROUTE_NAMES.ERGEBNISSE.ANGEMELDETE,
            () => import('@/views/pages/ergebnisse/Ergebnisse.vue'),
            () => ({
                probenAnsicht: ProbenAnsicht.Angemeldete
            })
        ),

        createRouteMitKunde(
            '/proben-in-bearbeitung',
            ROUTE_NAMES.ERGEBNISSE.IM_LABOR,
            () => import('@/views/pages/ergebnisse/Ergebnisse.vue'),
            () => ({
                probenAnsicht: ProbenAnsicht.ImLabor
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/ergebnisse/im-labor', ROUTE_NAMES.ERGEBNISSE.IM_LABOR),

        createRouteMitKunde(
            '/vorlaeufige-ergebnisse',
            ROUTE_NAMES.ERGEBNISSE.VORLAEUFIGE,
            () => import('@/views/pages/ergebnisse/Ergebnisse.vue'),
            () => ({
                probenAnsicht: ProbenAnsicht.VorlaufigeErgebnisse
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/ergebnisse/vorlaeufige', ROUTE_NAMES.ERGEBNISSE.VORLAEUFIGE),

        createRouteMitKunde(
            '/freigegebene-ergebnisse',
            ROUTE_NAMES.ERGEBNISSE.ENDGUELTIGE,
            () => import('@/views/pages/ergebnisse/Ergebnisse.vue'),
            () => ({
                probenAnsicht: ProbenAnsicht.EndgueltigeErgebnisse
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde('/ergebnisse/endgueltige', ROUTE_NAMES.ERGEBNISSE.ENDGUELTIGE),

        createRouteMitKunde('/ergebnisse/filter', ROUTE_NAMES.ERGEBNISSE.FILTER, () => import('@/views/pages/ergebnisse/ErgebnisseFilter.vue')),

        createRouteMitKunde('/ergebnisse/statistik', ROUTE_NAMES.ERGEBNISSE.STATISTIK, () => import('@/views/pages/ergebnisse/Statistik.vue')),

        createRouteMitKunde(
            `/proben-in-zustellung/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.ERGEBNISSE.DETAIL_ANGEMELDETE,
            () => import('@/views/pages/ergebnisse/Ergebnis.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                ansicht: ProbenAnsicht.Angemeldete
            })
        ),

        createRouteMitKunde(
            `/proben-in-bearbeitung/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.ERGEBNISSE.DETAIL_IM_LABOR,
            () => import('@/views/pages/ergebnisse/Ergebnis.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                ansicht: ProbenAnsicht.ImLabor
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde(`/ergebnisse/im-labor/:probenNr(${routeConstraints.numerischeId})`, ROUTE_NAMES.ERGEBNISSE.DETAIL_IM_LABOR),

        createRouteMitKunde(
            `/vorlaeufige-ergebnisse/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.ERGEBNISSE.DETAIL_VORLAEUFIGE,
            () => import('@/views/pages/ergebnisse/Ergebnis.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                ansicht: ProbenAnsicht.VorlaufigeErgebnisse
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde(`/ergebnisse/vorlaeufige/:probenNr(${routeConstraints.numerischeId})`, ROUTE_NAMES.ERGEBNISSE.DETAIL_VORLAEUFIGE),

        createRouteMitKunde(
            `/freigegebene-ergebnisse/:probenNr(${routeConstraints.numerischeId})`,
            ROUTE_NAMES.ERGEBNISSE.DETAIL_ENDGUELTIGE,
            () => import('@/views/pages/ergebnisse/Ergebnis.vue'),
            (route) => ({
                probenNr: parseInt(route.params.probenNr),
                ansicht: ProbenAnsicht.EndgueltigeErgebnisse
            })
        ),
        // bisherige Probenportal Url
        createRedirectRouteMitKunde(`/ergebnisse/endgueltige/:probenNr(${routeConstraints.numerischeId})`, ROUTE_NAMES.ERGEBNISSE.DETAIL_ENDGUELTIGE),

        createRouteMitKunde(
            '/suche',
            ROUTE_NAMES.ERGEBNISSE.SCHNELLSUCHE,
            () => import('@/views/pages/ergebnisse/Schnellsuche.vue'),
            (route) => ({
                suchtext: route.query.suchtext || null
            })
        ),
        // Legacy-Schnellsuche mit ProbenNr läuft über den Query-Parameter `SchnellsucheProbenNr`
        // auf `/ergebnisse/schnellsuche`.
        {
            path: '/:kunde?/ergebnisse/schnellsuche',
            component: () => import('@/views/pages/ergebnisse/Schnellsuche.vue'),
            props: (route) => ({
                kunde: route.params.kunde || null,
                probenNr: parseInt(route.query.SchnellsucheProbenNr!.toString())
            })
        }
    ];
}

/**
 * Erstellt alle Zertifikate-Routen
 */
export function createZertifikateRoutes(): RouteRecordRaw[] {
    return [createRouteMitKunde('/zertifikate/qualitaetskontrolle', ROUTE_NAMES.ZERTIFIKATE.QUALITAETSKONTROLLE, () => import('@/views/pages/zertifikate/Qualitaetskontrolle.vue'))];
}

/**
 * Erstellt alle Wissensdatenbank-Routen
 */
export function createWissensdatenbankRoutes(): RouteRecordRaw[] {
    return [createRouteMitKunde('/wissensdatenbank', ROUTE_NAMES.WISSENSDATENBANK.UEBERSICHT, () => import('@/views/pages/wissensdatenbank/Uebersicht.vue'))];
}

/**
 * Erstellt alle Seiten-Routen
 */
export function createPagesRouten(): RouteRecordRaw[] {
    return [createRouteMitKunde('/pages/empty', ROUTE_NAMES.PAGES.EMPTY, () => import('@/views/pages/Empty.vue')), createRouteMitKunde('/documentation', ROUTE_NAMES.PAGES.DOCUMENTATION, () => import('@/views/pages/Documentation.vue'))];
}

/**
 * Erstellt die Fehler-Routen
 * Diese werden einmal als Kind vom AppLayout erstellt und einmal außerhalb, falls noch keine Anmeldung vorhanden ist.
 */
export function createFehlerRouten(mitLayout: boolean): RouteRecordRaw[] {
    const accessName = mitLayout ? ROUTE_NAMES.ZUGRIFF_VERWEIGERT_EINGELOGGT : ROUTE_NAMES.ZUGRIFF_VERWEIGERT;
    const errorName = mitLayout ? ROUTE_NAMES.FEHLER_EINGELOGGT : ROUTE_NAMES.FEHLER;

    return [
        {
            path: mitLayout ? 'zugriff-verweigert' : '/zugriff-verweigert',
            name: accessName,
            component: () => import('@/views/pages/fehler/Access.vue'),
            props: (route) => ({ kunde: route.params.kunde || null }),
            // requiresAuth: false, damit die Fehlerseite auch ohne gültigen Auth-Kontext erreichbar ist.
            // kundendatenLadenUeberspringen: true, damit der Router-Guard hier keinen erneuten Kundendaten-Load auslöst.
            meta: { requiresAuth: false, ausstehendeAenderungenPruefungUeberspringen: true, kundendatenLadenUeberspringen: true }
        },
        {
            path: mitLayout ? 'fehler' : '/fehler',
            name: errorName,
            component: () => import('@/views/pages/fehler/Fehler.vue'),
            props: (route) => ({ kunde: route.params.kunde || null }),
            // requiresAuth: false, damit die Fehlerseite auch ohne gültigen Auth-Kontext erreichbar ist.
            // kundendatenLadenUeberspringen: true, damit der Router-Guard hier keinen erneuten Kundendaten-Load auslöst.
            meta: { requiresAuth: false, ausstehendeAenderungenPruefungUeberspringen: true, kundendatenLadenUeberspringen: true }
        }
    ];
}

/**
 * Erstellt die Dashboard-Route
 */
export function createDashboardRoute(): RouteRecordRaw {
    return createRouteMitKunde('', ROUTE_NAMES.DASHBOARD, () => import('@/views/Dashboard.vue'));
}

/**
 * Erstellt alle Auth-Routen (ohne Fehler-Route)
 */
export function createAuthRouten(): RouteRecordRaw[] {
    return [
        {
            path: '/login',
            name: ROUTE_NAMES.AUTH.LOGIN,
            component: () => import('@/views/pages/auth/Login.vue'),
            meta: { requiresAuth: false }
        }
    ];
}

/**
 * Erstellt die Hilfe-Route
 */
export function createHilfeRouten(): RouteRecordRaw[] {
    return [createRouteMitKunde('/hilfe', ROUTE_NAMES.HILFE, () => import('@/views/pages/Hilfe.vue'))];
}

/**
 * Typsichere Navigation Helper
 */
export const RouterHelpers = {
    checkHistoryStateVorhanden: async (erforderlicheKeys: string[], returnUrl: string | null, router: Router): Promise<boolean> => {
        const state = window.history.state ?? {};

        // Sind alle erforderlichen Keys vorhanden?
        if (erforderlicheKeys.every((key) => state[key] !== undefined && state[key] !== null)) {
            return true;
        }

        if (!returnUrl) {
            throw new Error('Es wurden nicht alle erforderlichen Historie-States übergeben und es war keine RetunUrl gesetzt');
        }

        await router.replace(returnUrl);
        return false;
    },

    // Dashboard
    toDashboard: (kunde?: string) => ({
        name: ROUTE_NAMES.DASHBOARD,
        params: kunde ? { kunde } : {}
    }),

    // Proben
    toProbenListe: (kunde?: string | null) => ({
        name: ROUTE_NAMES.PROBEN.LISTE,
        params: kunde ? { kunde } : {}
    }),
    toProbenNeu: (kunde?: string | null) => ({
        name: ROUTE_NAMES.PROBEN.NEU,
        params: kunde ? { kunde } : {}
    }),
    toProbenEdit: (probenNr: number, kunde?: string | null) => ({
        name: ROUTE_NAMES.PROBEN.EDIT,
        params: {
            probenNr: probenNr.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),
    toProbenNummernZuordnen: (kunde?: string | null, returnTo?: string) => ({
        name: ROUTE_NAMES.PROBEN.PROBENNUMMERN_ZUORDNEN,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {}
    }),
    toProbenExcelImport: (kunde?: string) => ({
        name: ROUTE_NAMES.PROBEN.EXCEL_IMPORT,
        params: kunde ? { kunde } : {}
    }),
    toTestzeitpunkt: (probenNr: number, kunde?: string) => ({
        name: ROUTE_NAMES.PROBEN.TESTZEITPUNKT_BEARBEITEN,
        params: {
            probenNr: probenNr.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),

    toNichtFreigegebeneProbenWarnung: (anzahlProben: number, anzahlGutachten: number, kunde?: string) => ({
        name: ROUTE_NAMES.PROBEN.NICHT_FREIGEGEBENE_WARNUNG,
        params: kunde ? { kunde } : {},
        state: {
            anzahlProben,
            anzahlGutachten
        }
    }),

    // Mustervorlagen
    toMustervorlagenListe: (kunde?: string | null) => ({
        name: ROUTE_NAMES.MUSTERVORLAGEN.LISTE,
        params: kunde ? { kunde } : {}
    }),
    toMustervorlagenNeu: (kunde?: string | null) => ({
        name: ROUTE_NAMES.MUSTERVORLAGEN.NEU,
        params: kunde ? { kunde } : {}
    }),
    toMustervorlagenEdit: (mustervorlagenNr: number, kunde?: string | null) => ({
        name: ROUTE_NAMES.MUSTERVORLAGEN.EDIT,
        params: {
            mustervorlagenNr: mustervorlagenNr.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),
    toMustervorlagenFilter: (filter: MustervorlagenFilter, kunde?: string, returnTo?: string) => ({
        name: ROUTE_NAMES.MUSTERVORLAGEN.FILTER,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {},
        state: {
            filter
        }
    }),

    // Etiketten
    toEtikettendruck: (kunde?: string) => ({
        name: ROUTE_NAMES.ETIKETTEN.LISTE,
        params: kunde ? { kunde } : {}
    }),
    toEtikettendruckFilter: (kunde?: string, returnTo?: string) => ({
        name: ROUTE_NAMES.ETIKETTEN.FILTER,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {}
    }),
    toProbenbegleitscheinDruck: (kunde?: string) => ({
        name: ROUTE_NAMES.PROBENBEGLEITSCHEIN.LISTE,
        params: kunde ? { kunde } : {}
    }),
    toProbenbegleitscheinDruckFilter: (kunde?: string, returnTo?: string) => ({
        name: ROUTE_NAMES.PROBENBEGLEITSCHEIN.FILTER,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {}
    }),

    // Wissensdatenbank
    toWissensdatenbank: (kunde?: string) => ({
        name: ROUTE_NAMES.WISSENSDATENBANK.UEBERSICHT,
        params: kunde ? { kunde } : {}
    }),

    // Begehungsberichte
    toBegehungsberichteListe: (kunde?: string) => ({
        name: ROUTE_NAMES.BEGEHUNGSBERICHTE.LISTE,
        params: kunde ? { kunde } : {}
    }),

    toBegehungsberichteDetail: (id: number, kunde?: string) => ({
        name: ROUTE_NAMES.BEGEHUNGSBERICHTE.DETAIL,
        params: {
            id: id.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),

    // Gutachten
    toGutachtenErfassen: (kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.ERFASSEN,
        params: kunde ? { kunde } : {}
    }),
    toGutachtenInBearbeitung: (kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.IN_BEARBEITUNG,
        params: kunde ? { kunde } : {}
    }),
    toGutachtenTypAuswahl: (kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.TYP_AUSWAHL,
        params: kunde ? { kunde } : {}
    }),
    toGutachtenNeu: (gutachtenTypID: GutachtenTyp, kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.NEU,
        params: {
            gutachtenTypID: gutachtenTypID,
            ...(kunde ? { kunde } : {})
        }
    }),
    toGutachtenBearbeiten: (id: number, kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.EDIT,
        params: {
            id: id.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),
    toGutachtenErgebnisse: (kunde?: string) => ({
        name: ROUTE_NAMES.GUTACHTEN.ERGEBNISSE,
        params: kunde ? { kunde } : {}
    }),
    toGutachtenRohstoffspezifikationen: (kunde?: string) => ({
        name: ROUTE_NAMES.GUTACHTEN.ROHSTOFFSPEZIFIKATIONEN,
        params: kunde ? { kunde } : {}
    }),
    toGutachtenFilter: (filter: GutachtenFilter, kunde?: string, returnTo?: string) => ({
        name: ROUTE_NAMES.GUTACHTEN.FILTER,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {},
        state: { filter }
    }),
    toGutachtenDetail: (id: number, kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.DETAIL,
        params: {
            id: id.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),
    toGutachtenInBearbeitungDetail: (id: number, kunde?: string | null) => ({
        name: ROUTE_NAMES.GUTACHTEN.DETAIL_IN_BEARBEITUNG,
        params: {
            id: id.toString(),
            ...(kunde ? { kunde } : {})
        }
    }),
    toGutachtenDetails: (ansicht: GutachtenAnsicht, id: number, kunde?: string | null) => {
        switch (ansicht) {
            case GutachtenAnsicht.Ergebnisse:
                return RouterHelpers.toGutachtenDetail(id, kunde);
            case GutachtenAnsicht.InBearbeitung:
                return RouterHelpers.toGutachtenInBearbeitungDetail(id, kunde);
            default:
                throw new Error('Unbekannte GutachtenAnsicht für Detailnavigation: ' + ansicht);
        }
    },

    // Textvorlagen
    toTextvorlagenFuerFeld: (probentyp: Probentyp, prefix: string, feldname: string, label: string, mehrzeilig: boolean, probenID?: number | null, kunde?: string, returnTo?: string) => ({
        name: probentyp === Probentyp.Probe ? ROUTE_NAMES.PROBEN.TEXTVORLAGEN : ROUTE_NAMES.MUSTERVORLAGEN.TEXTVORLAGEN,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {},
        state: {
            prefix: prefix,
            feldname: feldname,
            label: label,
            mehrzeilig: mehrzeilig,
            probenID: probenID
        }
    }),

    // Prüfpläne
    toPruefplaene: (feldname: string, probentyp: Probentyp, kunde?: string | null, returnTo?: string) => ({
        name: probentyp === Probentyp.Probe ? ROUTE_NAMES.PROBEN.PRUEFPLAENE : ROUTE_NAMES.MUSTERVORLAGEN.PRUEFPLAENE,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {},
        state: {
            zielFeldname: feldname
        }
    }),

    // Ergebnisse
    toErgebnisseEndgueltige: (kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.ENDGUELTIGE,
        params: kunde ? { kunde } : {}
    }),
    toErgebnisseVorlaeufige: (kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.VORLAEUFIGE,
        params: kunde ? { kunde } : {}
    }),
    toErgebnisseImLabor: (kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.IM_LABOR,
        params: kunde ? { kunde } : {}
    }),
    toErgebnisseAngemeldete: (kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.ANGEMELDETE,
        params: kunde ? { kunde } : {}
    }),
    toErgebnisseFilter: (ansicht: ProbenAnsicht, filter: any, kunde?: string, returnTo?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.FILTER,
        params: kunde ? { kunde } : {},
        query: returnTo ? { returnTo } : {},
        state: {
            ansicht,
            filter
        }
    }),
    toErgebnisseStatistik: (kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.STATISTIK,
        params: kunde
    }),

    // Detail-Navigation je Ansicht
    toErgebnisEndgueltigeDetail: (probenNr: number, kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.DETAIL_ENDGUELTIGE,
        params: { probenNr: probenNr.toString(), ...(kunde ? { kunde } : {}) }
    }),
    toErgebnisVorlaufigeDetail: (probenNr: number, kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.DETAIL_VORLAEUFIGE,
        params: { probenNr: probenNr.toString(), ...(kunde ? { kunde } : {}) }
    }),
    toErgebnisImLaborDetail: (probenNr: number, kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.DETAIL_IM_LABOR,
        params: { probenNr: probenNr.toString(), ...(kunde ? { kunde } : {}) }
    }),
    toErgebnisAngemeldeteDetail: (probenNr: number, kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.DETAIL_ANGEMELDETE,
        params: { probenNr: probenNr.toString(), ...(kunde ? { kunde } : {}) }
    }),
    toErgebnisDetails: (ansicht: ProbenAnsicht, probenNr: number, kunde?: string) => {
        switch (ansicht) {
            case ProbenAnsicht.EndgueltigeErgebnisse:
                return RouterHelpers.toErgebnisEndgueltigeDetail(probenNr, kunde);
            case ProbenAnsicht.VorlaufigeErgebnisse:
                return RouterHelpers.toErgebnisVorlaufigeDetail(probenNr, kunde);
            case ProbenAnsicht.ImLabor:
                return RouterHelpers.toErgebnisImLaborDetail(probenNr, kunde);
            case ProbenAnsicht.Angemeldete:
                return RouterHelpers.toErgebnisAngemeldeteDetail(probenNr, kunde);
            default:
                throw new Error('Unbekannte ProbenAnsicht für Detailnavigation: ' + ansicht);
        }
    },
    toSchnellsuche: (suchtext: string, kunde?: string) => ({
        name: ROUTE_NAMES.ERGEBNISSE.SCHNELLSUCHE,
        params: kunde ? { kunde } : {},
        query: { suchtext }
    }),

    toKundenbereich: (kunde?: string) => ({
        name: ROUTE_NAMES.KUNDENBEREICH.TABELLEN_ERGEBNIS,
        params: kunde ? { kunde } : {}
    }),

    // Auth
    toLogin: () => ({
        name: ROUTE_NAMES.AUTH.LOGIN
    }),
    toAccessDenied: (istEingeloggt: boolean, kunde?: string) => ({
        name: istEingeloggt ? ROUTE_NAMES.ZUGRIFF_VERWEIGERT_EINGELOGGT : ROUTE_NAMES.ZUGRIFF_VERWEIGERT,
        params: istEingeloggt ? { kunde: kunde || null } : {}
    }),
    toError: (istEingeloggt: boolean, kunde?: string) => ({
        name: istEingeloggt ? ROUTE_NAMES.FEHLER_EINGELOGGT : ROUTE_NAMES.FEHLER,
        params: istEingeloggt ? { kunde: kunde || null } : {}
    }),

    // Hilfe / Schnittstelle
    toHilfe: (kunde?: string) => ({
        name: ROUTE_NAMES.HILFE,
        params: kunde ? { kunde } : {}
    }),

    // Sonstige/Pages/UI Kit
    toDocumentation: (kunde?: string) => ({
        name: ROUTE_NAMES.PAGES.DOCUMENTATION,
        params: kunde ? { kunde } : {}
    }),
    toEmpty: (kunde?: string) => ({
        name: ROUTE_NAMES.PAGES.EMPTY,
        params: kunde ? { kunde } : {}
    }),
    toCharts: (kunde?: string) => ({
        name: ROUTE_NAMES.UIKIT.CHARTS,
        params: kunde ? { kunde } : {}
    }),

    // Typenbasierte Helper-Methoden
    /**
     * Navigiert zur "Neu"-Route basierend auf dem Probentyp
     */
    toNeu: (probentyp: Probentyp, kunde?: string | null) => {
        switch (probentyp) {
            case Probentyp.Probe:
                return RouterHelpers.toProbenNeu(kunde);
            case Probentyp.Mustervorlage:
                return RouterHelpers.toMustervorlagenNeu(kunde);
            default:
                ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
        }
    },

    /**
     * Navigiert zur "Liste"-Route basierend auf dem Probentyp
     */
    toListe: (probentyp: Probentyp, kunde?: string | null) => {
        switch (probentyp) {
            case Probentyp.Probe:
                return RouterHelpers.toProbenListe(kunde);
            case Probentyp.Mustervorlage:
                return RouterHelpers.toMustervorlagenListe(kunde);
            default:
                ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
        }
    },

    /**
     * Navigiert zur "Edit"-Route basierend auf dem Probentyp
     */
    toEdit: (probentyp: Probentyp, id: number, kunde?: string | null) => {
        switch (probentyp) {
            case Probentyp.Probe:
                return RouterHelpers.toProbenEdit(id, kunde);
            case Probentyp.Mustervorlage:
                return RouterHelpers.toMustervorlagenEdit(id, kunde);
            default:
                ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
        }
    },

    /**
     * Navigiert zur "Excel Import"-Route basierend auf dem Probentyp
     */
    toExcelImport: (probentyp: Probentyp, kunde?: string) => {
        switch (probentyp) {
            case Probentyp.Probe:
                return RouterHelpers.toProbenExcelImport(kunde);
            case Probentyp.Mustervorlage:
                throw new Error('Kein Excel-Import für Mustervorlagen');
            default:
                ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
        }
    },

    /**
     * Navigiert zur "Mischprobe bearbeiten"-Seite basierend auf dem Probentyp
     */
    toMischprobe: (probentyp: Probentyp, probenNr: number, kunde?: string | null) => {
        const route: string = (() => {
            switch (probentyp) {
                case Probentyp.Probe:
                    return ROUTE_NAMES.PROBEN.MISCHPROBE_BEARBEITEN;
                case Probentyp.Mustervorlage:
                    return ROUTE_NAMES.MUSTERVORLAGEN.MISCHPROBE_BEARBEITEN;
                default:
                    return ProbentypHelpers.throwUnbekannterProbentypError(probentyp);
            }
        })();

        return {
            name: route,
            params: {
                probenNr: probenNr.toString(),
                ...(kunde ? { kunde } : {})
            }
        };
    }
};
